'use client';

import { cva, type VariantProps } from 'class-variance-authority';
import { Tabs as TabsPrimitive } from 'radix-ui';
import { useEffect, useRef, type ReactNode } from 'react';

import { cn } from '@platform/ui/lib/utils';

/*
 * The strip and its tabs, exported so a set of route links that are sections of
 * one page draws exactly the same line (docs/11 §8). A Radix tab marks itself
 * aria-selected; a link marks itself aria-current.
 */
export const tabsListVariants = cva('flex scrollbar-none gap-5 overflow-x-auto border-b border-border [&::-webkit-scrollbar]:hidden');

export const tabsTriggerVariants = cva(
    'relative -mb-px inline-flex shrink-0 items-center gap-2 border-b-2 border-transparent py-3 text-14 whitespace-nowrap text-muted-foreground transition-colors outline-none hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring aria-selected:border-primary aria-selected:font-medium aria-selected:text-foreground aria-[current=page]:border-primary aria-[current=page]:font-medium aria-[current=page]:text-foreground',
);

const tabCountVariants = cva('inline-flex items-center rounded-badge px-2 text-12 tabular-nums', {
    variants: {
        tone: {
            neutral: 'bg-neutral-subtle text-muted-foreground',
            accent: 'bg-primary-subtle text-primary-strong',
            success: 'bg-success-subtle text-success-strong',
            warning: 'bg-warning-subtle text-warning-strong',
            danger: 'bg-destructive-subtle text-destructive-strong',
            info: 'bg-info-subtle text-info-strong',
        },
    },
    defaultVariants: {
        tone: 'neutral',
    },
});

export interface TabItem {
    id: string;
    label: ReactNode;
    /** A small count beside the label, e.g. the channels still missing a price. */
    badge?: ReactNode;
    badgeTone?: VariantProps<typeof tabCountVariants>['tone'];
}

/**
 * Equal-weight sections of one record, never a strip across the top of a list
 * (docs/11 §1). The content of the active tab is the children, so the tab and
 * its panel are tied together for assistive technology; arrow keys move
 * between tabs.
 */
export function Tabs({
    tabs,
    active,
    onChange,
    className,
    children,
}: {
    tabs: TabItem[];
    active: string;
    onChange: (id: string) => void;
    className?: string;
    children?: ReactNode;
}) {
    const list = useRef<HTMLDivElement>(null);

    // The strip scrolls on a phone; the selected tab must never be the one hidden
    // off its edge.
    useEffect(() => {
        list.current?.querySelector<HTMLElement>('[aria-selected="true"]')?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }, [active]);

    return (
        <TabsPrimitive.Root value={active} onValueChange={onChange} className={cn('flex flex-col gap-6', className)}>
            <TabsPrimitive.List ref={list} className={tabsListVariants()}>
                {/* Radix reports a change only when the value differs, but a click on
                    the tab already open is still a choice: a page that opens on a tab
                    picked from its data, as shipping does before any carrier, keeps
                    that tab after the data changes only if the click was reported. */}
                {tabs.map((tab) => (
                    <TabsPrimitive.Trigger
                        key={tab.id}
                        value={tab.id}
                        className={tabsTriggerVariants()}
                        onClick={tab.id === active ? () => onChange(tab.id) : undefined}
                    >
                        {tab.label}
                        {tab.badge != null && <span className={tabCountVariants({ tone: tab.badgeTone })}>{tab.badge}</span>}
                    </TabsPrimitive.Trigger>
                ))}
            </TabsPrimitive.List>
            {children !== undefined && (
                <TabsPrimitive.Content value={active} className="outline-none">
                    {children}
                </TabsPrimitive.Content>
            )}
        </TabsPrimitive.Root>
    );
}
